import React from "react";
import { useNavigate } from "react-router-dom";
import { MdChevronLeft, MdChevronRight, MdCalendarToday } from "react-icons/md";
import { useTrainingWeeks } from "../../hooks/useTrainingDays";
import TrainingDayCard from "./TrainingDayCard";
import EmptyState from "../common/EmptyState";
import LoadingSpinner from "../common/LoadingSpinner";

interface TrainingWeeksProps {
  programId: number;
  trainingDaysPerWeek?: number;
}

const TrainingWeeks: React.FC<TrainingWeeksProps> = ({
  programId,
  trainingDaysPerWeek,
}) => {
  const navigate = useNavigate();

  const {
    trainingWeeks,
    currentWeekIndex,
    setCurrentWeekIndex,
    totalWeeks,
    getCurrentWeek,
    goToPreviousWeek,
    goToNextWeek,
    getFilteredDaysForWeek,
    loading,
    error,
  } = useTrainingWeeks({
    programId,
    trainingDaysPerWeek,
  });

  const handleTrainingDayClick = (dayId: number, weekId: number) => {
    navigate(`/admin/programs/${programId}/weeks/${weekId}/days/${dayId}`);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="py-12 text-center">
        <div className="mb-4 text-lg text-red-500">{error}</div>
      </div>
    );
  }

  if (trainingWeeks.length === 0) {
    return (
      <EmptyState
        icon={MdCalendarToday}
        title="No Training Weeks"
        description="This program doesn't have any training weeks scheduled yet."
      />
    );
  }

  const currentWeek = getCurrentWeek();

  if (!currentWeek) {
    return null;
  }

  const displayedDays = getFilteredDaysForWeek(currentWeek);

  const trainingDaysCount = trainingDaysPerWeek || displayedDays.length;
  const restDaysCount = 7 - trainingDaysCount;

  return (
    <div className="space-y-6">
      {/* Week Navigation Header */}
      <div className="flex flex-col items-center justify-between md:flex-row">
        <div className="w-full pb-3 md:w-auto md:pb-0">
          <h2 className="text-xl font-bold text-navy-700 dark:text-white">
            Training Weeks
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Week {currentWeek.sequence_number} of {totalWeeks}
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={goToPreviousWeek}
            disabled={currentWeekIndex === 0}
            className="rounded-lg border border-gray-300 p-2 transition-colors hover:border-biafit-primary disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:hover:border-biafit-primary"
          >
            <MdChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>

          <div className="min-w-[120px] text-center">
            <div className="text-lg font-medium text-navy-700 dark:text-white">
              Week {currentWeek.sequence_number}
            </div>
          </div>

          <button
            onClick={goToNextWeek}
            disabled={currentWeekIndex === totalWeeks - 1}
            className="rounded-lg border border-gray-300 p-2 transition-colors hover:border-biafit-primary disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:hover:border-biafit-primary"
          >
            <MdChevronRight className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* Week Progress Indicator */}
      {totalWeeks > 1 && (
        <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
          <div
            className="h-2 rounded-full bg-biafit-primary transition-all duration-300"
            style={{ width: `${((currentWeekIndex + 1) / totalWeeks) * 100}%` }}
          />
        </div>
      )}

      {/* Training Days Grid */}
      <div
        className="hidden gap-4 xl:grid"
        style={{
          gridTemplateColumns: `repeat(${displayedDays.length}, minmax(0, 1fr))`,
        }}
      >
        {displayedDays.map((trainingDay: any, index: number) => {
          const absoluteDayNumber =
            (currentWeek.sequence_number - 1) * trainingDaysCount + index + 1;

          return (
            <TrainingDayCard
              key={trainingDay.id}
              trainingDay={trainingDay}
              absoluteDayNumber={absoluteDayNumber}
              onClick={
                trainingDay.is_rest_day
                  ? undefined
                  : () => handleTrainingDayClick(trainingDay.id, currentWeek.id)
              }
            />
          );
        })}
      </div>

      {/* Small screens */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:hidden">
        {displayedDays.map((trainingDay: any, index: number) => {
          const absoluteDayNumber =
            (currentWeek.sequence_number - 1) * trainingDaysCount + index + 1;

          return (
            <TrainingDayCard
              key={trainingDay.id}
              trainingDay={trainingDay}
              absoluteDayNumber={absoluteDayNumber}
              onClick={
                trainingDay.is_rest_day
                  ? undefined
                  : () => handleTrainingDayClick(trainingDay.id, currentWeek.id)
              }
            />
          );
        })}
      </div>

      {/* Week Summary */}
      <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-800">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-biafit-primary">
              {trainingDaysCount}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Training Days
            </div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
              {restDaysCount}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Rest Days
            </div>
          </div>
          <div>
            <div className="text-2xl font-bold text-navy-700 dark:text-white">
              {totalWeeks}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total Weeks
            </div>
          </div>
        </div>
      </div>

      {/* Quick Week Navigator for many weeks */}
      {totalWeeks > 1 && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-600 dark:bg-navy-700">
          <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
            Quick Navigation
          </h3>
          <div className="flex flex-wrap gap-2">
            {trainingWeeks.map((week, index) => (
              <button
                key={week.id}
                onClick={() => setCurrentWeekIndex(index)}
                className={`rounded-md px-3 py-1 text-sm transition-colors ${
                  index === currentWeekIndex
                    ? "bg-biafit-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                }`}
              >
                Week {week.sequence_number}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingWeeks;
